<template>
    <b-card no-body>
        <div class="p-1 border-bottom d-flex flex-wrap align-items-center">
            <span class="mr-50 text-muted small">{{ $t('v4.Period') }}</span>
            <b-form-select
                v-model="fromMonth"
                :options="monthOptions"
                size="sm"
                class="w-auto mr-50"
                :disabled="!monthOptions.length"
            />
            <span class="mx-25 text-muted">~</span>
            <b-form-select
                v-model="toMonth"
                :options="monthOptions"
                size="sm"
                class="w-auto mr-1"
                :disabled="!monthOptions.length"
            />
            <span
                v-if="periodInfo"
                class="small"
                :class="periodError ? 'text-danger' : 'text-muted'"
            >{{ periodInfo }}</span>

            <b-form-checkbox
                v-model="selectAll"
                class="ml-auto"
                :disabled="!accounts.length"
                @change="handleSelectAll"
            >
                {{ $t('v4.Select All Accounts') }}
            </b-form-checkbox>
        </div>

        <b-table-simple responsive class="m-0">
            <colgroup>
                <col width="46px">
                <col>
            </colgroup>
            <b-thead>
                <b-tr>
                    <b-th></b-th>
                    <b-th>{{ $t('v4.Account') }}</b-th>
                </b-tr>
            </b-thead>
            <b-tbody v-if="!accounts.length">
                <b-tr>
                    <b-td colspan="2" class="text-center pt-2 pb-50">
                        {{ $t('org.message.No Data') }}
                    </b-td>
                </b-tr>
            </b-tbody>
            <b-tbody v-else>
                <b-tr
                    v-for="account in accounts"
                    :key="account.usageAccountId"
                >
                    <b-td>
                        <b-form-checkbox
                            v-model="selectedAccounts"
                            :value="account.usageAccountId"
                        />
                    </b-td>
                    <b-td>
                        {{ account.projectName }}
                        <span class="text-muted">({{ account.usageAccountId }})</span>
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <div class="p-1 border-top">
            <!--
                제한 사항을 요청 전에 보여준다. 받고 나서 "왜 메일에 파일이 없지"는 이미 늦다.
                한도·보관 기간은 서버가 내려준 값이라 설정을 바꿔도 안내가 어긋나지 않는다.
                이번 요청이 한도를 넘을지는 예고하지 않는다 — 만들어봐야 알 수 있다(BO-964).
            -->
            <b-alert
                v-if="limitNotice"
                show
                variant="secondary"
                class="p-50 small mb-1"
            >
                <i class="fas fa-info-circle mr-25"></i>
                {{ limitNotice }}
            </b-alert>

            <div class="d-flex align-items-center">
                <b-button
                    size="sm"
                    variant="primary"
                    class="ml-auto"
                    :disabled="!canRequest"
                    @click="submit"
                >
                    <i
                        v-if="requesting"
                        class="fas fa-spinner fa-spin mr-25"
                    ></i>
                    {{ $t('v4.Request') }}
                </b-button>
            </div>

            <div
                v-if="blockReason"
                class="small text-danger mt-50"
            >
                {{ blockReason }}
            </div>
        </div>
    </b-card>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from '@vue/composition-api'
import { formatBytes } from '@core/utils/filter'

const STORE_KEY = '$_invoice'
const MAX_MONTHS = 12

/** 'YYYY-MM' 을 비교 가능한 정수로. 문자열 비교로도 되지만 의도를 분명히 한다. */
const monthValue = (m) => (m ? Number(m.replace('-', '')) : 0)

/** from~to 사이 개월 수(양끝 포함). */
const monthSpan = (from, to) => {
    if (!from || !to) return 0
    const [fy, fm] = from.split('-').map(Number)
    const [ty, tm] = to.split('-').map(Number)
    return (ty - fy) * 12 + (tm - fm) + 1
}

export default {
    name: 'BulkDownloadForm',
    props: {
        groupIdx: {
            type: Number,
            default: null
        },
        accounts: {
            type: Array,
            default: () => []
        },
        userEmail: {
            type: String,
            default: ''
        },
        /** 요청 내역. 같은 조건이 처리 중인지 판정하는 데만 쓴다. */
        jobs: {
            type: Array,
            default: () => []
        }
    },
    setup (props, { emit }) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store

        const latestMonth = computed(() => store.getters[`${STORE_KEY}/bulkLatestMonth`])
        const requestInfo = computed(() => store.getters[`${STORE_KEY}/bulkRequestInfo`] || {})
        const requesting = computed(() => store.getters[`${STORE_KEY}/statusBulkRequest`] === 'loading')

        const fromMonth = ref(null)
        const toMonth = ref(null)
        const selectedAccounts = ref([])
        const selectAll = ref(false)

        /**
         * 상시 안내 문구. 첨부 한도와 보관 기간을 요청 전에 알린다.
         *
         * 두 값 모두 서버가 내려준 것이라 설정을 바꿔도 안내가 어긋나지 않는다.
         * 아직 못 받았으면 문구를 만들지 않는다 — 빈 값이 들어간 안내는 안 하느니만 못하다.
         */
        const limitNotice = computed(() => {
            const { mailAttachLimitBytes, expireDays } = requestInfo.value
            const limit = formatBytes(mailAttachLimitBytes)
            if (!limit || !expireDays) return ''
            return vm.$t('v4.Email attachment is supported up to {limit}', { limit, days: expireDays })
        })

        /**
         * 선택 가능한 월 목록. 상한은 그룹의 최신 발행월이다.
         * 미래월이나 아직 발행되지 않은 월을 고르면 서버가 거부하므로 애초에 노출하지 않는다.
         */
        const monthOptions = computed(() => {
            if (!latestMonth.value) return []
            const options = []
            let [year, month] = latestMonth.value.split('-').map(Number)
            // 상한부터 24개월치만 내려간다. 12개월 제한이 있어 그 이상은 고를 이유가 없다.
            for (let i = 0; i < 24; i++) {
                const value = `${year}-${String(month).padStart(2, '0')}`
                options.push({ value, text: value })
                month -= 1
                if (month === 0) {
                    month = 12
                    year -= 1
                }
            }
            return options
        })

        // 최신 발행월을 받으면 기본 기간을 그 달 하나로 잡는다.
        watch(latestMonth, (value) => {
            if (!value) {
                fromMonth.value = null
                toMonth.value = null
                return
            }
            fromMonth.value = value
            toMonth.value = value
        }, { immediate: true })

        // 그룹이 바뀌면 계정 선택을 비운다. 이전 그룹 계정이 남으면 권한 오류가 난다.
        watch(() => props.groupIdx, () => {
            selectedAccounts.value = []
            selectAll.value = false
        })

        watch(selectedAccounts, (value) => {
            selectAll.value = props.accounts.length > 0 && value.length === props.accounts.length
        })

        const handleSelectAll = (checked) => {
            selectedAccounts.value = checked
                ? props.accounts.map(item => item.usageAccountId)
                : []
        }

        const span = computed(() => monthSpan(fromMonth.value, toMonth.value))
        const periodError = computed(() => {
            if (!fromMonth.value || !toMonth.value) return false
            return monthValue(fromMonth.value) > monthValue(toMonth.value) || span.value > MAX_MONTHS
        })
        const periodInfo = computed(() => {
            if (!fromMonth.value || !toMonth.value) return ''
            if (monthValue(fromMonth.value) > monthValue(toMonth.value)) {
                return vm.$t('v4.Start month is later than end month')
            }
            if (span.value > MAX_MONTHS) {
                return vm.$t('v4.Up to {count} months at a time', { count: MAX_MONTHS })
            }
            return vm.$t('v4.{count} months', { count: span.value })
        })

        /**
         * 지금 고른 조건과 똑같은 요청이 이미 처리 중인가.
         *
         * 서버의 중복 판정 키(그룹 · 기간 · 계정 집합 · 언어)를 그대로 옮긴 것이다.
         * 이걸 안 막으면 사용자는 버튼을 누른 뒤에야 "이미 처리 중입니다" 오류를 본다.
         *
         * 진행 중인 요청이 하나라도 있으면 전부 막는 방식은 쓰지 않는다 — 생성이
         * 분~시간 단위라, A그룹을 요청해 두고 B그룹을 못 넣는 상황이 된다.
         * 정상적인 사용이므로 같은 조건일 때만 막는다.
         *
         * 판정 근거는 이미 폴링으로 받고 있는 요청 목록이라 새로고침해도 복원된다.
         */
        const duplicatedJob = computed(() => {
            const mine = [...selectedAccounts.value].sort()
            return (props.jobs || []).find((job) => {
                if (job.status !== 'WAITING' && job.status !== 'RUNNING') return false
                if (job.groupIdx !== props.groupIdx) return false
                if (job.startMonth !== fromMonth.value || job.endMonth !== toMonth.value) return false
                if (job.lang !== vm.$i18n.locale) return false
                const theirs = [...(job.accountIds || [])].sort()
                return theirs.length === mine.length && theirs.every((id, i) => id === mine[i])
            })
        })

        /** 요청을 막는 첫 번째 사유. 버튼만 비활성하면 왜 안 되는지 알 수 없다. */
        const blockReason = computed(() => {
            if (!props.groupIdx) return vm.$t('v4.Select a group first')
            if (!latestMonth.value) return vm.$t('v4.No issued invoice in this group')
            if (periodError.value) return periodInfo.value
            if (!selectedAccounts.value.length) return vm.$t('v4.Select at least one account')
            if (duplicatedJob.value) return vm.$t('v4.The same request is already in progress')
            return ''
        })
        const canRequest = computed(() => !blockReason.value && !requesting.value)

        const submit = () => {
            if (!canRequest.value) return
            emit('submit', {
                groupIdx: props.groupIdx,
                payload: {
                    startMonth: fromMonth.value,
                    endMonth: toMonth.value,
                    accountIds: selectedAccounts.value,
                    lang: vm.$i18n.locale
                }
            })
        }

        return {
            fromMonth,
            toMonth,
            monthOptions,
            selectedAccounts,
            selectAll,
            handleSelectAll,
            limitNotice,
            periodInfo,
            periodError,
            blockReason,
            canRequest,
            requesting,
            submit
        }
    }
}
</script>
